import { BusinessData } from '../types/business';

// Using type definition directly to avoid import issues
type InvoiceItem = {
  id: number;
  name: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  inventoryItemId?: string;
  hsnCode?: string;
  gstRate?: number;
};

type Invoice = {
  id: string;
  invoiceNumber: string;
  client: string;
  clientId?: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
  };
  date: string;
  dueDate: string;
  paymentDate?: string;
  items: InvoiceItem[];
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  taxable: boolean;
  discountType: string;
  discountValue: number;
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  createdAt: string;
  userId: string;
  paymentInfo?: {
    amountReceived: number;
    paymentMode: 'cash' | 'bank';
    bankAccount?: {
      bank: string;
      accountNumber: string;
      ifscCode?: string; // Updated to match usage
    };
    paymentDate: string;
    paymentReference?: string;
  };
  template?: InvoiceTemplateInterface;
};

// Define the NEW enum for templates
export enum InvoiceTemplateType {
  TRADITIONAL = 'traditional',
  FORMAL = 'formal',
  MODERN = 'modern',
  FUTURISTIC = 'futuristic',
  CUSTOM = 'custom',
  // Keeping 'STANDARD' mapped to TRADITIONAL for backward compat if needed temporarily, or removing if we want clean break.
  // Let's keep a fallback for old saved data
  STANDARD = 'traditional',
}

// Interface for the new template system
export interface InvoiceTemplateInterface {
  id: number;
  name: string;
  image?: string;
  description: string;
  variant: 'traditional' | 'formal' | 'modern' | 'futuristic' | 'custom';
  colorScheme?: {
    primary: string;
    secondary: string;
    accent?: string;
    text?: string;
    background?: string;
  };
  layout?: {
    header: 'standard' | 'centered' | 'split';
    footer: 'standard' | 'minimal' | 'detailed';
    itemsTable: 'standard' | 'compact' | 'detailed';
  };
  fontFamily?: string;
  showLogo?: boolean;
  showWatermark?: boolean;
}

// Default business details for preview
export const defaultBusinessDetails: any = {
  name: 'AkMo Technologies',
  address: '123 Business Park',
  city: 'Mumbai',
  state: 'Maharashtra',
  pincode: '400001',
  gstin: '27AABCT1234Z1Z5',
  phone: '+91 9876543210',
  email: 'info@akmotechnologies.com',
  bankDetails: {
    bankName: 'HDFC Bank',
    accountNumber: '12345678901234',
    ifscCode: 'HDFC0001234',
  },
  logoUrl: 'https://via.placeholder.com/150x60?text=AkMo+Tech',
};

// Default mock invoice for previews
export const defaultMockInvoice = {
  id: 'mock-1',
  invoiceNumber: 'INV-001',
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  client: 'Sample Client',
  clientId: 'client-1',
  clientDetails: {
    name: 'Sample Client',
    address: '456 Client Street, Client City',
    gstin: '29ABCDE1234F1Z5',
    email: 'client@example.com',
    phone: '+91 9876543210',
  },
  items: [
    {
      id: 1,
      name: 'Web Development Services',
      description: 'Website design and development',
      quantity: 1,
      rate: 25000,
      amount: 25000,
      hsnCode: '998314',
      gstRate: 18
    },
    {
      id: 2,
      name: 'Hosting Services (Annual)',
      description: 'Cloud hosting with maintenance',
      quantity: 1,
      rate: 12000,
      amount: 12000,
      hsnCode: '998316',
      gstRate: 18
    }
  ],
  subtotal: 37000,
  taxRate: 18,
  tax: 6660,
  amount: 43660,
  status: 'draft',
  createdAt: new Date().toISOString(),
  userId: 'user-1',
  taxType: 'IGST',
  taxable: true,
  discountType: 'percentage',
  discountValue: 0,
  discount: 0,
  notes: 'Thank you for your business!',
  termsAndConditions: 'Payment due within 15 days.'
};

// Define the NEW invoice templates
export const invoiceTemplateOptions: InvoiceTemplateInterface[] = [
  {
    id: 1,
    name: 'Traditional',
    description: 'Classic, serif-based design suitable for legal and conservative businesses.',
    variant: 'traditional',
    colorScheme: {
      primary: '#000000',
      secondary: '#333333',
      accent: '#666666',
      text: '#000000',
      background: '#FFFFFF'
    },
    layout: {
      header: 'centered',
      footer: 'minimal',
      itemsTable: 'standard'
    },
    fontFamily: 'Times-Roman',
    showLogo: true,
    showWatermark: false
  },
  {
    id: 2,
    name: 'Formal',
    description: 'Structured, professional layout with clear partitions. Ideal for corporate invoicing.',
    variant: 'formal',
    colorScheme: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
      accent: '#bdc3c7',
      text: '#2c3e50',
      background: '#ffffff'
    },
    layout: {
      header: 'standard',
      footer: 'detailed',
      itemsTable: 'detailed'
    },
    fontFamily: 'Helvetica',
    showLogo: true,
    showWatermark: false
  },
  {
    id: 3,
    name: 'Modern',
    description: 'Clean lines, sans-serif typography, and subtle color accents. Best for tech and creative agencies.',
    variant: 'modern',
    colorScheme: {
      primary: '#2563eb', // Blue
      secondary: '#3b82f6',
      accent: '#eff6ff',
      text: '#1f2937',
      background: '#ffffff'
    },
    layout: {
      header: 'split',
      footer: 'standard',
      itemsTable: 'compact'
    },
    fontFamily: 'Helvetica', // Will use Roboto/Open Sans in PDF generator
    showLogo: true,
    showWatermark: false
  },
  {
    id: 4,
    name: 'Futuristic',
    description: 'Bold headers, sharp contrasts, and distinctive layout. Stands out from the crowd.',
    variant: 'futuristic',
    colorScheme: {
      primary: '#0F172A', // Dark Slate
      secondary: '#475569',
      accent: '#F8FAFC',
      text: '#0F172A',
      background: '#FFFFFF'
    },
    layout: {
      header: 'split',
      footer: 'detailed',
      itemsTable: 'detailed'
    },
    fontFamily: 'Helvetica-Bold', // Placeholder
    showLogo: true,
    showWatermark: true
  }
];

// Helper to get preference with fallback
export const getTemplatePreference = async (userId: string): Promise<InvoiceTemplateType | number> => {
  // In a real app, this would fetch from indexedDB or API
  // For now, return default
  return 1;
};

export const saveTemplatePreference = async (userId: string, templateType: InvoiceTemplateType | number) => {
  // Mock save
  console.log(`Saved preference for ${userId}: ${templateType}`);
  return;
};

export const getTemplatePreferenceSync = (): InvoiceTemplateType | number => {
  return 1;
};

// Function placeholder to prevent breaking imports of generateInvoiceHTML
// We will return a basic string or throw an error, as we are moving to PDF-only or React-based preview
export const generateInvoiceHTML = (invoice: Invoice, profile: any, template: InvoiceTemplateType): string => {
  console.warn("generateInvoiceHTML is deprecated. Please use InvoicePDFGenerator.");
  return "<html><body><p>HTML Preview is deprecated. Please use the PDF View.</p></body></html>";
};
